import type { CSSProperties } from 'react';
import { Search, SlidersHorizontal, Users, X, CheckCircle2 } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import type { ObservacoesProfessorPageContext } from './useObservacoesProfessorPage';

type Props = Pick<
  ObservacoesProfessorPageContext,
  | 'showAlunoPicker'
  | 'setShowAlunoPicker'
  | 'alunoSearch'
  | 'setAlunoSearch'
  | 'pickerTurmaFilter'
  | 'setPickerTurmaFilter'
  | 'showPickerFilter'
  | 'setShowPickerFilter'
  | 'meusAlunos'
  | 'minhasTurmas'
  | 'form'
  | 'setForm'
> & {
  variant: 'sheet' | 'modal';
};

function chipBtn(active: boolean): CSSProperties {
  return {
    padding: '4px 10px',
    borderRadius: 20,
    border: 'none',
    background: active ? '#065f46' : 'var(--bg-main, #f3f4f6)',
    color: active ? '#fff' : 'var(--text-main, #374151)',
    fontSize: '0.62rem',
    fontWeight: 700,
    cursor: 'pointer',
  };
}

function AlunoPickerContent(props: Props) {
  const {
    alunoSearch,
    setAlunoSearch,
    pickerTurmaFilter,
    setPickerTurmaFilter,
    showPickerFilter,
    setShowPickerFilter,
    meusAlunos,
    minhasTurmas,
    form,
    setForm,
    setShowAlunoPicker,
  } = props;

  const searchLower = alunoSearch.toLowerCase();
  const filtered = meusAlunos.filter((a) => {
    if (pickerTurmaFilter && a.turmaId !== pickerTurmaFilter) return false;
    if (!searchLower) return true;
    return (a.nome || '').toLowerCase().includes(searchLower);
  });
  const activeTurmaName = pickerTurmaFilter
    ? minhasTurmas.find((t) => t.id === pickerTurmaFilter)?.nome
    : null;

  return (
    <>
      <div className="obs-picker-toolbar">
        <div className="obs-picker-search-wrap">
          <Search size={14} />
          <input
            type="search"
            value={alunoSearch}
            onChange={(e) => setAlunoSearch(e.target.value)}
            placeholder="Buscar por nome..."
          />
        </div>
        <button
          type="button"
          className={`obs-filter-toggle ${showPickerFilter || pickerTurmaFilter ? 'active' : ''}`}
          onClick={() => setShowPickerFilter(!showPickerFilter)}
          aria-label="Filtrar turma"
        >
          <SlidersHorizontal size={16} />
        </button>
      </div>
      {showPickerFilter && (
        <div className="obs-picker-chips">
          <button type="button" onClick={() => setPickerTurmaFilter('')} style={chipBtn(!pickerTurmaFilter)}>
            Todas
          </button>
          {minhasTurmas.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setPickerTurmaFilter(t.id === pickerTurmaFilter ? '' : t.id)}
              style={chipBtn(pickerTurmaFilter === t.id)}
            >
              {t.nome}
            </button>
          ))}
        </div>
      )}
      <p className="obs-picker-count">
        {filtered.length} aluno{filtered.length !== 1 ? 's' : ''}
        {activeTurmaName ? ` · ${activeTurmaName}` : ''}
      </p>
      <div className="obs-picker-list">
        {filtered.length === 0 ? (
          <div className="obs-picker-empty">
            <Users size={28} style={{ opacity: 0.3 }} />
            <p>Nenhum aluno encontrado</p>
          </div>
        ) : (
          filtered.map((aluno) => {
            const isSelected = form.alunoId === aluno.id;
            const iniciais = aluno.nome
              .split(' ')
              .filter(Boolean)
              .slice(0, 2)
              .map((p) => p[0])
              .join('')
              .toUpperCase();
            return (
              <button
                key={aluno.id}
                type="button"
                className={`obs-picker-row${isSelected ? ' obs-picker-row--on' : ''}`}
                onClick={() => {
                  setForm({ ...form, alunoId: aluno.id });
                  setShowAlunoPicker(false);
                }}
              >
                <span className="obs-picker-row-avatar">{iniciais}</span>
                <span className="obs-picker-row-info">
                  <span className="obs-picker-row-name">{aluno.nome}</span>
                  {aluno.turma?.nome && (
                    <span className="obs-picker-row-turma">{aluno.turma.nome}</span>
                  )}
                </span>
                {isSelected && <CheckCircle2 size={16} style={{ color: '#059669' }} />}
              </button>
            );
          })
        )}
      </div>
    </>
  );
}

export function ObservacoesAlunoPicker(props: Props) {
  const { showAlunoPicker, setShowAlunoPicker, variant } = props;

  if (!showAlunoPicker) return null;

  if (variant === 'modal') {
    return (
      <Modal
        open={showAlunoPicker}
        onClose={() => setShowAlunoPicker(false)}
        title="Selecionar aluno"
        size="lg"
        contentClassName="obs-desk-picker-modal"
      >
        <AlunoPickerContent {...props} />
      </Modal>
    );
  }

  return (
    <div
      className="obs-detalhe-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={(e) => e.target === e.currentTarget && setShowAlunoPicker(false)}
      role="presentation"
    >
      <div
        className="obs-detalhe-sheet obs-picker-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <div className="obs-picker-handle" aria-hidden />
        <div className="obs-picker-sheet-header">
          <h2>Selecionar Aluno</h2>
          <button type="button" onClick={() => setShowAlunoPicker(false)} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>
        <AlunoPickerContent {...props} />
      </div>
    </div>
  );
}
